import { Injectable, inject } from '@angular/core';
import { Router } from '@angular/router';
import { Auth, Hub } from 'aws-amplify';
import AWS from 'aws-sdk';
import { detectEnv } from './Enviroment';
import { GlobalDataService } from './global-data.service';

import { BehaviorSubject, map } from 'rxjs';

export interface UserExtraData {
  name: string;
  email: string;
  phone_number: string;
  locale: string;
  "custom:signalwire_ok": string;
  middle_name: string;
  "custom:address": string;
  "custom:country": string;

  "custom:ciudad": string;
  "custom:provincia": string;
  "custom:postcode": string;
  "custom:passport": string;

  "phone_number_verified": boolean ;
}

export interface AuthStatus {
  loggedIn: boolean;
  username?: string | null;
  id?: string | null;
  email?: string | null;
  name?: string | null;
  phone_number?: string | null;
  middle_name?: string | null;
  address?: string | '';
  country?: string | '';

  ciudad?: string | '';
  postcode?: string | '';
  passport?: string | '';
  phone_number_verified?: boolean;
}

const EMPTY_LOGGED: AuthStatus = {
  loggedIn: false,
  username: '',
  id: '',
  email: '',
  name: '',
  phone_number: '',
  middle_name: '',
  address: '',
  country: '',

  ciudad: '',
  postcode: '',
  passport: '',
  phone_number_verified: undefined,
};

@Injectable({
  providedIn: 'root',
})
export class AuthService {
  router = inject(Router);
  private globalDataService = inject(GlobalDataService);
  private authStatusSubject = new BehaviorSubject<AuthStatus>(EMPTY_LOGGED);
  public authStatus$ = this.authStatusSubject.asObservable();

  // Observable para tracking de inicialización completa
  private initializedSubject = new BehaviorSubject<boolean>(false);
  public initialized$ = this.initializedSubject.asObservable();

  private sns = new AWS.SNS();

  readonly isLoggedIn$ = this.authStatus$.pipe(map((state) => state.loggedIn));
  protected currentEnv = detectEnv();

  constructor() {
    this.initAuth();

    Hub.listen('auth', async ({ payload: { event, data, message } }) => {
      switch (event) {
        case 'signedIn':
        case 'signIn':
        case 'autoSignIn':
          // this.setUpUser(data);
          break;
        case 'updateUserAttributes':
          //console.log('updateUserAttributes');
          break;
        case 'signedOut':
          //console.log('user have been signedOut successfully.');
          await this.updateUserStateOffline();
          break;
        case 'tokenRefresh':
          //console.log('auth tokens have been refreshed.');
          break;
        case 'tokenRefresh_failure':
          //console.log('failure while refreshing auth tokens.');
          await this.updateUserStateOffline();
          break;
        case 'signInWithRedirect':
          //console.log('signInWithRedirect API has successfully been resolved.');
          break;
        case 'signInWithRedirect_failure':
          //console.log('failure while trying to resolve signInWithRedirect API.');
          await this.updateUserStateOffline();
          break;
        case 'customOAuthState':
          //console.info('custom state returned from CognitoHosted UI');
          break;
        default:
          //await this.updateUserStateOffline();
          this.authStatusSubject.next(EMPTY_LOGGED);
      }
    });
  }

  private async initAuth(): Promise<void> {
    // Tiempo máximo de espera para la comprobación inicial de sesión.
    // Si Cognito tarda más (reintentos internos con refresh token expirado),
    // marcamos como no autenticado y limpiamos la sesión local.
    const AUTH_TIMEOUT_MS = 5000;
    const timeoutPromise = new Promise<never>((_, reject) =>
      setTimeout(() => reject(new Error('auth_timeout')), AUTH_TIMEOUT_MS)
    );

    try {
      const user = await Promise.race([
        Auth.currentAuthenticatedUser(),
        timeoutPromise,
      ]);
      this.setUser(user);
    } catch (err: any) {
      const isExpiredOrTimeout =
        err?.code === 'NotAuthorizedException' ||
        err?.name === 'NotAuthorizedException' ||
        err?.message?.includes('Refresh Token has expired') ||
        err?.message === 'auth_timeout';

      if (isExpiredOrTimeout) {
        // Limpiar tokens expirados del localStorage para evitar reintentos en futuras cargas
        Auth.signOut().catch(() => {});
      }
      this.authStatusSubject.next(EMPTY_LOGGED);
    } finally {
      this.initializedSubject.next(true);
    }
  }

  private setUpUser(data: any) {
    this.setUser(data);
    this.router.navigate(['home'], { replaceUrl: true });
  }

  private updateUserData(data: any) {
    const currentUser = this.getUser();
    this.authStatusSubject.next({ ...currentUser, ...data });
  }

  getUser() {
    return this.authStatusSubject.getValue();
  }

  private userDetailsCache: any = null;

  setUserDetails(data: any): void {
    this.userDetailsCache = data;
  }

  getUserDetails(): any {
    return this.userDetailsCache;
  }

  clearUserDetails(): void {
    this.userDetailsCache = null;
  }

  private setUser(user: any) {
    if (!user) {
      return;
    }

    const {
      attributes: { sub: id, email, name, phone_number, middle_name, address, ciudad, country, postcode, passport, phone_number_verified },
      username,
    } = user;

    this.authStatusSubject.next({
      loggedIn: true,
      id,
      username,
      email,
      name,
      phone_number,
      middle_name,
      address,
      country,

      ciudad,
      postcode,
      passport,
      phone_number_verified: phone_number_verified === true || phone_number_verified === 'true',
    });
  }

  async login(username: string, password: string) {

    this.authStatusSubject.next(EMPTY_LOGGED);
    try {

      // const allowed = await this.checkUserAccessByEnv(username, this.currentEnv);
      // console.log(allowed)
      // if (!allowed) {
      //   throw new Error(`Este usuario no está autorizado para acceder al entorno ${this.currentEnv.toUpperCase()}`);
      // }
      const user = await Auth.signIn(username, password);
      const userAttributes = await Auth.userAttributes(user);

      // const estadoAttribute = userAttributes.find(
      //   (attr) => attr.Name === 'custom:Estado'
      // );
      // if (estadoAttribute && estadoAttribute.Value === 'ONLINE') {
      //   await Auth.signOut();
      //   throw new Error('Your account is already online, please close the other session.');
      // }
      // 🔐 Validación de entorno permitido (aquí ya tenemos los atributos)
      const portal = userAttributes.find(attr => attr.Name === 'custom:Portal');
      const portals = portal?.Value?.split(',').map(e => e.trim().toLowerCase()) || [];
      // console.log("Login portal: ",portals)
      localStorage.setItem("userPortal", portals.toString());
      localStorage.setItem("PhoneNumber", username);


      const allowedEnvsAttr = userAttributes.find(attr => attr.Name === 'custom:allowed_envs');
      const allowedEnvs = allowedEnvsAttr?.Value?.split(',').map(e => e.trim().toLowerCase()) || [];
      localStorage.setItem("allowedEnvs", allowedEnvs.toString());

      const env = this.currentEnv; // 'dev' | 'prod' según tu dominio oauth

      if (!allowedEnvs.includes(env)) {
        await Auth.signOut();
        throw new Error(`No tienes permiso para entrar a este entorno `);//${env.toUpperCase()}
      }

      const suspendidoAttribute = userAttributes.find(
        (attr) => attr.Name === 'custom:Banned'
      );
      if (suspendidoAttribute && suspendidoAttribute.Value === '1') {
        await Auth.signOut();
        throw new Error('Your account has been suspended. Please contact support.');
      }

      //await this.updateUserState(user, 'ONLINE');
      this.setUser(user);
      return user;
    } catch (error) {
      throw error;
    }
  }

  async logout() {
    try {
      const user = await Auth.currentAuthenticatedUser();
      // localStorage.removeItem('selectedPortal');
      // localStorage.removeItem('userPortal');
      // Borrar todo el localStorage
      localStorage.clear();
      if( user ) await this.updateUserState(user, 'OFFLINE');
    } catch (error) {
      //console.error('Error updating user state to OFFLINE:', error);
    }
    this.globalDataService.invalidateUserEsims();
    await Auth.signOut();
    this.authStatusSubject.next({ loggedIn: false });
    this.router.navigate(['onboarding/login']);
    //window.location.reload();
  }

  async updateUserState(user: any, state: string) {
    try {
      const userAttributes = await Auth.userAttributes(user);
      const estadoAttribute = userAttributes.find(
        (attr) => attr.Name === 'custom:Estado'
      );

      if (estadoAttribute) {
        await Auth.updateUserAttributes(user, {
          'custom:Estado': state,
        });
      } else {
        await Auth.updateUserAttributes(user, {
          'custom:Estado': state,
        });
      }
    } catch (error) {
      //console.error(`Error updating user state to ${state}:`, error);
    }
  }

  // private async checkUserAccessByEnv(username: string, env: 'dev' | 'prod' | 'unknown'): Promise<boolean> {
  //   if (env === 'unknown') return false;

  //   try {
  //     const user = await Auth.signIn(username); // solo para obtener atributos, sin password
  //     const attributes = await Auth.userAttributes(user);

  //     const allowedEnvsAttr = attributes.find(attr => attr.Name === 'custom:allowed_envs');
  //     const allowedEnvs = allowedEnvsAttr?.Value?.split(',').map(e => e.trim().toLowerCase()) || [];

  //     await Auth.signOut(); // Importante: cerrar sesión si solo estabas leyendo atributos

  //     return allowedEnvs.includes(env);
  //   } catch (error) {
  //     console.error("Error verificando entornos permitidos:", error);
  //     return false;
  //   }
  // }

  private async updateUserStateOffline() {
    try {
      const user = await Auth.currentAuthenticatedUser();
      await this.updateUserState(user, 'OFFLINE');
    } catch (error) {
      //console.error('Error updating user state to OFFLINE:', error);
    }
  }

  async register(username: string, password: string,code: string) {
    this.authStatusSubject.next(EMPTY_LOGGED);
    const attributes = {
      email: '',
      'custom:Partnercode':"",
      'custom:Estado': '',
      'custom:country': '',
      'custom:address': '',
      'custom:ciudad': '',
      'custom:provincia': '',
      'custom:passport': '',
      'custom:postcode': '',
    };
     if (code !== "") {
        attributes['custom:Partnercode'] = code; // Agregar el código a los atributos
    }
    return await Auth.signUp({
      username: username.toLowerCase(),
      password,
      attributes,
      autoSignIn: {
        enabled: true,
      },
    });
  }

  async resetPassword(username: string): Promise<void> {
    return await Auth.forgotPassword(username);
  }

  async confirmSignUp(username: string, validationCode: string) {
    this.authStatusSubject.next(EMPTY_LOGGED);
    return await Auth.confirmSignUp(username, validationCode);
  }

  resendSignUp(username: string) {
    return Auth.resendSignUp(username);
  }

  confimacion(username: string){
    const params = {
      Message: 'Tu código de verificación es: 123456',
      PhoneNumber: username, // Número de teléfono en formato internacional
    };

    this.sns.publish(params, (err, data) => {
      if (err) console.error(err, err.stack);
      else console.log(data);
    });
  }

  async forgotPasswordSubmit(username: string, code: string, newPassword: string) {
    this.authStatusSubject.next(EMPTY_LOGGED);
    return await Auth.forgotPasswordSubmit(username, code, newPassword);
  }

  async deleteAccount() {
    await Auth.deleteUser();
    this.authStatusSubject.next({ loggedIn: false });
    this.router.navigate(['onboarding/login']);
    window.location.reload();
  }

  async updateUser(data: Partial<UserExtraData>) {
    const user = await Auth.currentAuthenticatedUser();
    await Auth.updateUserAttributes(user, data);
    this.updateUserData(data);
  }

  async refresh() {
    try {
      const user = await Auth.currentAuthenticatedUser({ bypassCache: true });
      this.setUser(user);
    } catch (_err) {
      // ignore refresh errors
    }
  }
}
